import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bettingService } from '../../services/bettingAPI';
import UserLayout from './UserLayout'; // Import your new layout
import '../../styles/user/AccountStatement.css';

const AccountStatement = () => {
    const navigate = useNavigate();
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('all');
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [summary, setSummary] = useState({
        totalDeposits: 0,
        totalWithdrawals: 0,
        totalWins: 0,
        totalLosses: 0,
        netProfit: 0,
        closingBalance: 0,
        startingBalance: 0
    });
    const [expandedRows, setExpandedRows] = useState({});

    useEffect(() => {
        loadTransactions();
    }, [filter, dateRange]);

    const loadTransactions = async () => {
        try {
            setLoading(true);
            
            const params = {
                page: 1,
                limit: 1000,
                ...(filter !== 'all' && { type: filter }),
                ...(dateRange.start && { fromDate: dateRange.start }),
                ...(dateRange.end && { toDate: dateRange.end })
            };
            
            const response = await bettingService.getAccountStatement(params);
            
            if (response?.success && response?.data) {
                setTransactions(response.data.transactions);
                setSummary(response.data.summary);
            } else {
                throw new Error('Invalid response from server');
            }
            
            setError(null);
        } catch (err) {
            console.error('Error loading account statement:', err);
            setError('Failed to load account statement');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return '₹0';
        const prefix = amount > 0 ? '+' : '';
        return `${prefix}₹${amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Get bet type display based on marketType and betType
    const getBetTypeDisplay = (betDetails) => {
        if (!betDetails) return '-';
        
        const { marketType, betType } = betDetails;
        
        // Session bets: show YES/NO
        if (marketType === 'session') {
            return betType === 'yes' ? 'YES' : 'NO';
        }
        // Bookmaker bets: show BACK/LAY
        else if (marketType === 'bookmaker' || marketType === 'bookmaker2' || marketType === 'bookmaker_tied') {
            return betType === 'back' ? 'BACK' : 'LAY';
        }
        
        return '-';
    };

    // Get bet type badge class
    const getBetTypeClass = (betDetails) => {
        if (!betDetails) return '';
        
        const { marketType, betType } = betDetails;
        
        if (marketType === 'session') {
            return betType === 'yes' ? 'bet-type-yes' : 'bet-type-no';
        } else if (marketType === 'bookmaker' || marketType === 'bookmaker2' || marketType === 'bookmaker_tied') {
            return betType === 'back' ? 'bet-type-back' : 'bet-type-lay';
        }
        
        return 'bet-type-na';
    };

    const toggleExpandRow = (id) => {
        setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const exportToCSV = () => {
        const headers = ['Date', 'Type', 'Bet Type', 'Match Name', 'Description', 'Amount', 'Running Balance'];
        const csvRows = [headers];
        
        transactions.forEach(t => {
            const betType = t.betDetails ? getBetTypeDisplay(t.betDetails) : '-';
            const matchName = t.betDetails?.matchName || '-';
            csvRows.push([
                formatDate(t.date),
                t.type.toUpperCase(),
                betType,
                matchName,
                t.description,
                t.amount,
                t.runningBalance || 0
            ]);
        });
        
        const csvContent = csvRows.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `account_statement_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <UserLayout activeTab="AccountStatement">
            <div className="account-statement-page" style={{ width: '100%' }}>
                
                {/* Filters */}
                <div className="statement-filters">
                    <div className="filter-control">
                        <select value={filter} onChange={(e) => setFilter(e.target.value)}>
                            <option value="all">All</option>
                            <option value="deposit">Deposits</option>
                            <option value="withdrawal">Withdrawals</option>
                            <option value="win">Wins</option>
                            <option value="loss">Losses</option>
                        </select>
                    </div>

                    <div className="filter-control">
                        <input 
                            type="date" 
                            value={dateRange.start}
                            onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                        />
                    </div>

                    <div className="filter-control">
                        <input 
                            type="date" 
                            value={dateRange.end}
                            onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                        />
                    </div>

                    <button 
                        className="filter-refresh"
                        onClick={loadTransactions}
                        disabled={loading}
                    >
                        ↻
                    </button>
                </div>

                {/* Loading State */}
                {loading ? (
                    <div className="loading-state">Loading transactions...</div>
                ) : error ? (
                    <div className="error-state">{error}</div>
                ) : transactions.length === 0 ? (
                    <div className="empty-state">No transactions found</div>
                ) : (
                    <div className="txn-table-wrapper">
                        <table className="txn-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Type</th>
                                    <th>Bet Type</th>
                                    <th>Match Name</th>
                                    <th>Description</th>
                                    <th>Amount</th>
                                    <th>Balance</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.map((t, i) => (
                                    <React.Fragment key={t.id || i}>
                                        <tr className={t.betDetails?.matchName ? 'has-match-name' : ''}>
                                            <td className="txn-date">{formatDate(t.date)}</td>
                                            <td>
                                                <span className="txn-badge">
                                                    {t.type.toUpperCase()}
                                                </span>
                                            </td>
                                            <td>
                                                {t.betDetails ? (
                                                    <span className={`bet-type-badge ${getBetTypeClass(t.betDetails)}`}>
                                                        {getBetTypeDisplay(t.betDetails)}
                                                    </span>
                                                ) : (
                                                    <span className="bet-type-badge bet-type-na">-</span>
                                                )}
                                            </td>
                                            <td className="txn-match-name">
                                                {t.betDetails?.matchName ? (
                                                    <span className="match-name-badge">
                                                        {t.betDetails.matchName}
                                                    </span>
                                                ) : (
                                                    <span className="match-name-na">-</span>
                                                )}
                                            </td>
                                            <td className="txn-desc">{t.description}</td>
                                            <td className={t.amount >= 0 ? "txn-positive" : "txn-negative"}>
                                                {formatCurrency(t.amount)}
                                            </td>
                                            <td>{formatCurrency(t.runningBalance)}</td>
                                            <td>
                                                {t.betDetails && (
                                                    <button 
                                                        className="txn-expand-btn"
                                                        onClick={() => toggleExpandRow(t.id)}
                                                    >
                                                        {expandedRows[t.id] ? "▲" : "▼"}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>

                                        {expandedRows[t.id] && t.betDetails && (
                                            <tr className="txn-expanded">
                                                <td colSpan="8">
                                                    <div className="txn-expanded-box">
                                                        <div className="txn-details-grid">
                                                            <div>
                                                                <span>Match</span>
                                                                <br/>
                                                                <strong>{t.betDetails.matchName || 'N/A'}</strong>
                                                            </div>
                                                            <div>
                                                                <span>Market</span>
                                                                <br/>
                                                                {t.betDetails.marketName}
                                                            </div>
                                                            <div>
                                                                <span>Selection</span>
                                                                <br/>
                                                                {t.betDetails.selectionName}
                                                            </div>
                                                            <div>
                                                                <span>Market Type</span>
                                                                <br/>
                                                                {t.betDetails.marketType?.toUpperCase()}
                                                            </div>
                                                            <div>
                                                                <span>Bet Type</span>
                                                                <br/>
                                                                <span className={`bet-type-badge ${getBetTypeClass(t.betDetails)}`}>
                                                                    {getBetTypeDisplay(t.betDetails)}
                                                                </span>
                                                            </div>
                                                            <div>
                                                                <span>Odds</span>
                                                                <br/>
                                                                {t.betDetails.odds}
                                                            </div>
                                                            <div>
                                                                <span>Stake</span>
                                                                <br/>
                                                                ₹{t.betDetails.stake?.toLocaleString()}
                                                            </div>
                                                            {t.betDetails.potentialWin && (
                                                                <div>
                                                                    <span>Potential Win</span>
                                                                    <br/>
                                                                    <span className="positive">
                                                                        ₹{t.betDetails.potentialWin.toLocaleString()}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {t.betDetails.targetValue && (
                                                                <div>
                                                                    <span>Target</span>
                                                                    <br/>
                                                                    {t.betDetails.comparison} {t.betDetails.targetValue}
                                                                </div>
                                                            )}
                                                            {t.betDetails.actualResult && (
                                                                <div>
                                                                    <span>Actual Result</span>
                                                                    <br/>
                                                                    {t.betDetails.actualResult}
                                                                </div>
                                                            )}
                                                            <div>
                                                                <span>Result</span>
                                                                <br/>
                                                                <span className={t.betDetails.result === 'win' ? 'positive' : 'negative'}>
                                                                    {t.betDetails.result?.toUpperCase()}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </UserLayout>
    );
};

export default AccountStatement;